<template>
  <b-modal id="bv-modal-download-album-choice"
           size="md"
           centered
           hide-header
           :no-close-on-backdrop="downloading"
           :no-close-on-esc="downloading"
           @hidden="resetState">
    <div class="text-center" v-if="!downloading">
      <Icon name="grDownload" :width="36" class="mb-3"/>
      <h1 class="modal-title mb-2">{{ $t('modal_download_album_choice.title') }}</h1>
      <h4 class="modal-subtitle text-secondary" v-if="showDirectDownload">{{ $t('modal_download_album_choice.subtitle') }}</h4>
    </div>

    <div class="text-center py-3" v-else>
      <b-spinner class="mb-3"/>
      <p class="font-weight-medium">{{ progressText }}</p>
    </div>

    <template #modal-footer="{ cancel }">
      <div class="w-100" v-if="!downloading">
        <b-button variant="primary" block @click="downloadDirect" class="mb-2" :disabled="!estimatedCount" v-if="showDirectDownload">
          <Icon name="download" :width="18" class="mr-1" vertical-align="sub"/>
          {{ $t('modal_download_album_choice.action_direct') }}
        </b-button>
        <b-button variant="light" block @click="downloadEmail" class="mb-2">
          <Icon name="send" :width="18" class="mr-1" vertical-align="sub"/>
          {{ $t('modal_download_album_choice.action_email') }}
        </b-button>
        <b-button variant="link" block @click="cancel()" class="text-secondary">
          {{ $t('modal_download_album_choice.action_cancel') }}
        </b-button>
      </div>
      <div class="w-100" v-else>
        <b-button variant="light" block @click="cancelDownload" :disabled="cancelling">
          {{ cancelling ? $t('modal_download_album_choice.action_cancelling') : $t('modal_download_album_choice.action_cancel_download') }}
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import EventService from "@/services/Event";
import {DOWNLOAD_STAGE, downloadMedias} from "@/domain/galleries/medias/mediaDownload";

export default {
  name: "ModalDownloadAlbumChoice",

  props: {
    estimatedCount: {
      type: Number,
      default: 0,
    },
    loadMedias: {
      type: Function,
      required: true,
    },
    checkin: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      downloading: false,
      cancelling: false,
      abortController: null,
      progressStage: DOWNLOAD_STAGE.PREPARING,
      progressCurrent: 0,
      progressTotal: 0,
    }
  },

  computed: {
    effectiveCheckin() {
      return this.checkin || this.$store.getters['checkinDetail/checkin']
    },
    progressText() {
      const key = this.progressStage === DOWNLOAD_STAGE.DOWNLOADING
          ? 'modal_download_album_choice.downloading_progress'
          : 'modal_download_album_choice.preparing_progress'
      return this.$t(key, {current: this.progressCurrent, total: this.progressTotal})
    },
    showDirectDownload() {
      return this.estimatedCount && this.estimatedCount < 50
    },
  },

  methods: {
    downloadEmail() {
      EventService.downloadAlbum(this.effectiveCheckin.id)
          .then(() => {
            this.$bvToast.toast(
                this.$t('toast_download_album.text'),
                {
                  title: this.$t('toast_download_album.title'),
                  headerClass: 'd-block pt-2',
                  noCloseButton: false,
                  autoHideDelay: 5000
                })
            this.$bvModal.hide('bv-modal-download-album-choice')
          })
          .catch(error => {
            if (error.response && error.response.status === 400 &&
                (error.response.data.code === 'bulk_download_not_available' || error.response.data.code === 'bulk_download_missing_hd')) {
              this.$bvToast.toast(
                  this.$t('toast_download_album.error'),
                  {variant: 'warning', autoHideDelay: 8000, html: true})
            }
            this.$bvModal.hide('bv-modal-download-album-choice')
          })
    },

    async downloadDirect() {
      if (!this.estimatedCount) return

      this.downloading = true
      this.cancelling = false
      this.abortController = new AbortController()
      this.progressStage = DOWNLOAD_STAGE.PREPARING
      this.progressCurrent = 0
      this.progressTotal = this.estimatedCount

      const signal = this.abortController.signal

      try {
        const medias = await this.loadMedias()
        if (!medias || medias.length === 0) return
        if (signal.aborted) return

        this.progressTotal = medias.length

        await downloadMedias(medias, {
          signal,
          onProgress: ({stage, current, total}) => {
            this.progressStage = stage
            this.progressCurrent = current
            this.progressTotal = total
          },
        })

        if (signal.aborted) {
          this.$bvToast.toast(
              this.$t('modal_download_album_choice.toast_cancelled'),
              {autoHideDelay: 4000})
        } else {
          this.$bvToast.toast(
              this.$t('modal_download_album_choice.toast_complete'),
              {
                title: this.$t('modal_download_album_choice.toast_complete_title'),
                headerClass: 'd-block pt-2',
                autoHideDelay: 5000
              })
        }
      } catch (error) {
        if (!signal.aborted) {
          this.$bvToast.toast(
              this.$t('modal_download_album_choice.toast_error'),
              {variant: 'danger', autoHideDelay: 5000})
        }
      } finally {
        this.downloading = false
        this.cancelling = false
        this.abortController = null
        this.$bvModal.hide('bv-modal-download-album-choice')
      }
    },

    cancelDownload() {
      if (!this.abortController || this.cancelling) return
      this.cancelling = true
      this.abortController.abort()
    },

    resetState() {
      this.downloading = false
      this.cancelling = false
      if (this.abortController) {
        this.abortController.abort()
        this.abortController = null
      }
      this.progressStage = DOWNLOAD_STAGE.PREPARING
      this.progressCurrent = 0
      this.progressTotal = 0
    },
  },
}
</script>
